import {
  ApiCustomer,
  ApiLineItem,
  ApiRegister,
  ApiSale,
  ApiUser,
} from '@vend/types'
import camelCase from 'lodash/camelCase'
import forEach from 'lodash/forEach'
import isArray from 'lodash/isArray'
import isPlainObject from 'lodash/isPlainObject'
import replace from 'lodash/replace'
import VendNumber, { add, multiply, vn, isFinite, subtract } from 'vend-number'
import { PAYMENT_TYPES } from '@vend/business-components'
import { DATE_MEDIUM_FULL, formatDateTime } from '@vend/utilities'
import { toHoursAndMinutes } from '../util/agenda-utils'
import {
  SHORT_CODE_CHARS,
  SHORT_CODE_LENGTH,
  WALKIN_CUSTOMER_CODE,
} from '../components/sale/sale-model.constant'
import { FulfilmentType, isDefinedFulfilmentType } from '../models/fulfilment'
import { LineItem } from '../models/line-item'
import {
  NON_CASH_FEE,
  NON_CASH_FEE_MUTATION,
  PayAccountBalance,
  Sale,
  SaleJson,
  TIP_ADJUSTMENT,
  TIP_ADJUSTMENT_MUTATION,
} from '../models/sale'
import {
  ASK_FOR_NOTE_STATUSES,
  CLOSED_STATUSES,
  COMPLETED_STATUSES,
  CONFIRMED_STATUSES,
  OPEN_STATUSES,
  SaleStatus,
  SaleSyncStatus,
  SYNCED_SYNC_STATUSES,
  UNFULFILLED_STATUSES,
  User,
} from '../models/sale.constants'
import { ReduxCurrentSale } from '../state/slices/currentSale/currentSaleSlice'
import { SaleDiscount } from '../models/sale-discount'
import { Payment, paymentHasTip } from '../models/payment'
import { isEligibleForCashDiscount } from '../sale-calculation/calculate-payment'
import { GqlAdjustment } from '../queries/sale_create'
import { lineItemsAdapter } from '../state/slices/currentSale/lineItemEntityAdapter'
import { ReduxSaleStorageAdapter } from '../state/slices/currentSale/reduxSaleStorageAdapter'
import { HIDDEN_PAYMENT_TYPES } from '../components/payments/integrated-payment/integrated-payment-types.constant'
import {
  getOnHandProductQuantities,
  getTotalProductQuantities,
  isGiftCard,
} from './lineItem'
import { hasChange } from './payments'
import {
  isServiceStatusFinal,
  hasServiceOrderWithAgenda,
  hasServiceOrder,
} from './service'

export interface PromotionSummaryItem {
  id: string | null
  name: string | null
  total: number | null
  description: string | null
  promo_code: string | null
}

export const getPromotionSummary = (
  lineItems: LineItem[]
): PromotionSummaryItem[] => {
  const promotionsList: PromotionSummaryItem[] = []
  if (lineItems) {
    lineItems.forEach(item => {
      if (item.promotionDetails) {
        const promotionDetails = item.promotionDetails

        promotionDetails.forEach(details => {
          const existingPromotion = promotionsList.find(
            p => p.id === details.id
          )
          const quantityAmount = multiply(vn(details.amount), vn(item.quantity))

          if (existingPromotion && existingPromotion.total != null) {
            existingPromotion.total += quantityAmount
          } else {
            promotionsList.push({
              id: details.id,
              name: details.name,
              total: quantityAmount,
              description: details.description,
              promo_code: details.promoCode,
            })
          }
        })
      }
    })
  }
  return promotionsList
}

/**
 * Get the sale total quantity by type (negative or positive quantities)
 *
 * @param  sale
 *         The sale to total up.
 *
 * @param  negative
 *         Look up all negative quantities if true. Positive quantities otherwise.
 *
 * @return Returns the quantity of products found defined by the quantity type. If no type is set the sum
 *         of all positive quantity line items will be returned
 *
 */
export const getTotalQuantityByType = (
  sale: Pick<Sale, 'lineItems'>,
  negative = false
): number => {
  if (!sale || !sale.lineItems) {
    return 0
  }

  return sale.lineItems.reduce((totalQuantity: number, lineItem: LineItem) => {
    if (vn(lineItem.quantity).isNegative() === negative) {
      return add(totalQuantity, lineItem.quantity)
    }
    return totalQuantity
  }, 0)
}

/**
 * Determine if the current sale is an exchange
 * Confirmation page uses a different definition of exchange as the designs should match sales that are return
 * exchanges as well as sales that have both positive and negative quantities but are not marked as return
 *
 * Definition: positive line item quantities > 0 and negative line item quantities < 0
 *
 * @param  sale
 *         The sale to check.
 *
 * @return True if the current sale is an exchange, false otherwise
 */
export const saleIsExchange = (sale: Pick<Sale, 'lineItems'>) => {
  if (!sale || !sale.lineItems) {
    return false
  }

  const totalPositiveQuantity = getTotalQuantityByType(sale)
  if (vn(totalPositiveQuantity).lte(0)) {
    return false
  }

  const totalNegativeQuantity = getTotalQuantityByType(sale, true)
  return vn(totalNegativeQuantity).lt(0)
}

/**
 * Determine if we should display the sale note.
 *
 * @return True if the sale note should be shown, false otherwise
 */
export const shouldDisplaySaleNote = (
  register: ApiRegister,
  sale: Pick<Sale, 'displayStatus'>
) => {
  const registerNotePreference = register && register.ask_for_note_on_save
  switch (registerNotePreference) {
    // Layby/Account/Return
    case 1:
      return Boolean(
        sale &&
          sale.displayStatus &&
          ASK_FOR_NOTE_STATUSES.indexOf(sale.displayStatus) >= 0
      )
    // All sales
    case 2:
      return true
  }
  return false
}

/**
 * Determines a more granular status for a sale than the API provides based on return heuristics
 *
 * @param  sale
 *         The sale API json object, or a sale model object
 *
 * @return The display status
 */
export const determineDisplayStatus = (sale: Sale | ApiSale) => {
  // Return the existing sale status if there is no return information.
  if (!(sale as ApiSale).return_for && !(sale as Sale).returnFor) {
    return sale.status
  }

  const lineItems: Array<ApiLineItem | LineItem> =
    (sale as ApiSale).line_items || (sale as Sale).lineItems || []
  const numLineItems = lineItems.length
  const numReturnLineItems = lineItems.filter(lineItem => {
    return (
      (lineItem as LineItem).isReturn || (lineItem as ApiLineItem).is_return
    )
  }).length

  // A sale is deemed an exchange if there are non return line items
  const isExchange = numLineItems > numReturnLineItems

  return determineReturnStatus(sale.status as SaleStatus, isExchange)
}

export const determineDisplayStatusRedux = (
  sale: Pick<ReduxCurrentSale, 'returnFor' | 'status'>,
  lineItems: ReadonlyArray<Readonly<Pick<LineItem, 'isReturn'>>>
) => {
  // Return the existing sale status if there is no return information.
  if (!sale.returnFor) {
    return sale.status
  }

  const numLineItems = lineItems.length
  const numReturnLineItems = lineItems.filter(lineItem => {
    return lineItem.isReturn
  }).length

  // A sale is deemed an exchange if there are non return line items
  const isExchange = numLineItems > numReturnLineItems

  return determineReturnStatus(sale.status, isExchange)
}

const determineReturnStatus = (
  status: SaleStatus | undefined,
  isExchange: boolean
) => {
  // Only parked and completed sale statuses should be considered exchanges or returns
  if (status === SaleStatus.PARKED) {
    if (isExchange) {
      return SaleStatus.EXCHANGE_PARKED
    }
    return SaleStatus.RETURN_PARKED
  }

  if (status === SaleStatus.CLOSED) {
    if (isExchange) {
      return SaleStatus.EXCHANGE_CLOSED
    }
    return SaleStatus.RETURN_CLOSED
  }

  // We shouldn't get here, but don't consider a confirmed layby or confirmed account sale a return or exchange.
  return status
}

export const isSalePickable = (lineItems: LineItem[], outletId: string) => {
  return new Promise<boolean>((resolve, reject) => {
    // Ignore products which do not have their inventory tracked;
    // `has_inventory` does *not* indicate a positive inventory count
    const productsWithTrackedInventory = lineItems.filter(
      l => l.product.has_inventory
    )

    // Sale is pickable when there are no products with tracked inventory
    if (!productsWithTrackedInventory.length) {
      return resolve(true)
    }

    const inSaleProductQtys = getTotalProductQuantities(
      productsWithTrackedInventory
    )

    getOnHandProductQuantities(productsWithTrackedInventory, outletId)
      .then(onHandProductQtys => {
        const isSalePickable = Object.keys(inSaleProductQtys).every(id =>
          vn(onHandProductQtys[id]).gte(inSaleProductQtys[id])
        )
        resolve(isSalePickable)
      })
      .catch(reject)
  })
}

export const convertSnakeToCamelCase = async (
  data: any,
  capitalID: boolean = true
) => {
  const camelCaseObject = Object.assign({}, data)

  await forEach(camelCaseObject, async (value: any, key) => {
    if (isPlainObject(value)) {
      value = await convertSnakeToCamelCase(value, capitalID)
    }

    if (isArray(value)) {
      const newArray: any[] = []
      forEach(value, async item => {
        newArray.push(await convertSnakeToCamelCase(item, capitalID))
      })
      value = newArray
    }

    const newKey = capitalID
      ? replace(camelCase(key), 'Id', 'ID')
      : camelCase(key)
    camelCaseObject[newKey] = value

    if (key.toString().indexOf('_') > 0) {
      delete camelCaseObject[key]
    }
  })

  return camelCaseObject
}

export const isUnfulfilled = (status: SaleStatus | undefined): boolean => {
  if (!status) {
    return false
  }
  return UNFULFILLED_STATUSES.includes(status as SaleStatus)
}

export type ApiUserToSaleUserParams = Pick<
  ApiUser,
  'id' | 'username' | 'display_name' | 'roles'
>
/**
 * Converts a User API representation to a User as defined in the Sale model,
 * by picking only the required fields.
 */
export const apiUserToSaleUser = (user: ApiUserToSaleUserParams): User => {
  return {
    id: user.id,
    username: user.username,
    display_name: user.display_name,
    roles: user.roles,
  }
}

/**
 * Determine if the sale line items has any fulfilments that are completed
 *
 * @returns {Boolean} True if the sale has any fulfilments that are completed
 */
export const hasFulfilmentsCompleted = (lineItems: LineItem[]): boolean => {
  return lineItems.some(lineItem => !!lineItem.fulfilmentCompleted)
}

/**
 * Determine if the sale has fulfilment details
 *
 * @returns {Boolean} True if the sale has fulfilment details
 */
export const hasFulfilmentDetails = (
  sale: Sale | ReduxCurrentSale
): boolean => {
  return (
    !!sale.fulfilmentDetails &&
    sale.fulfilmentDetails.length > 0 &&
    !!sale.fulfilmentDetails[0].type
  )
}

/**
 * Determine if the sale has any line items with fulfilment, excluding UNDEFINED
 *
 * @returns {Boolean} True if the sale has any line items with fulfilment
 */
export const hasLineItemFulfilment = (lineItems: LineItem[]): boolean => {
  return lineItems.some(
    lineItem =>
      !!lineItem.fulfilmentType &&
      lineItem.fulfilmentType !== FulfilmentType.UNSPECIFIED
  )
}

/**
 * Return true if the sale has a valid (non-WALKIN) customer suitable for display.
 *
 * @return True if the sale has a valid customer
 */
export const isValidCustomer = (
  customer?: ApiCustomer | null
): customer is ApiCustomer => {
  return Boolean(customer && customer.customer_code !== WALKIN_CUSTOMER_CODE)
}

export const hasValidCustomerWithEmail = (customer: ApiCustomer | null) => {
  return Boolean(isValidCustomer(customer) && customer.email)
}

/**
 * Determines if a customer can be removed from a sale.
 *
 * @param sale - The current sale object.
 * @returns A boolean indicating whether the customer can be removed from the sale.
 */
export const canRemoveCustomerFromSale = (
  sale: Pick<
    ReduxCurrentSale,
    | 'customer'
    | 'status'
    | 'payments'
    | 'payAccountBalance'
    | 'serviceFields'
    | 'customFields'
  >
) => {
  return Boolean(
    sale.customer &&
      isValidCustomer(sale.customer) &&
      !isCompleted(sale.status) &&
      !isConfirmed(sale) &&
      !hasCustomerRelatedPayments(sale.payments || []) &&
      !isUnfulfilled(sale.status) &&
      !isAccountBalancePaymentPaid(sale.payAccountBalance) &&
      !(
        isLegacyServiceSale(sale.status) ||
        hasServiceOrder(sale.serviceFields, sale.customFields)
      )
  )
}

/**
 * Used to check if the sale has any customer related payemnts
 * This will include store credits and loyalty
 */
export const hasCustomerRelatedPayments = (payments: Readonly<Payment[]>) => {
  return payments.some(payment => {
    const typeId = payment.paymentType.type_id
    return (
      typeId === PAYMENT_TYPES.STORE_CREDIT || typeId === PAYMENT_TYPES.LOYALTY
    )
  })
}

/**
 * Determine if the customer / line items / note are empty
 *
 * @return True if the sell form is empty, false otherwise.
 */
export const isEmptySale = (
  sale: Pick<ReduxCurrentSale, 'customer' | 'note' | 'lineItems'>
): boolean => {
  return (
    !isValidCustomer(sale.customer) &&
    !sale.note &&
    (!sale.lineItems?.ids || sale.lineItems.ids.length === 0)
  )
}

/**
 * Determine whether this sale is a return sale or not.
 *
 * @return true if the sale is a return, false otherwise
 */
export const isReturn = (
  sale: Pick<ReduxCurrentSale, 'returnFor'>
): boolean => {
  return Boolean(sale.returnFor)
}

/**
 * Return true if the sale is a blind return: at least one line item quantity it negative on a new sale.
 */
export const isBlindReturn = (sale?: ReduxCurrentSale) => {
  if (sale && isReturn(sale)) {
    return false
  }

  return sale?.lineItems.ids.some(
    id =>
      isFinite(sale?.lineItems.entities[id]?.quantity) &&
      vn(sale?.lineItems.entities[id]?.quantity).lt(0)
  )
}

/**
 * Check if the ABP (Account Balance Payment) has been paid.
 */
export const isAccountBalancePaymentPaid = (
  payAccountBalance?: PayAccountBalance | null
): boolean => {
  return Boolean(payAccountBalance?.amount && payAccountBalance?.paymentId)
}

/**
 * Check if the sale has added ABP (Account Balance Payment).
 */
export const isAccountBalancePaymentAdded = (
  payAccountBalance?: PayAccountBalance | null
) => {
  return Boolean(payAccountBalance?.amount)
}

/**
 * Determine whether this sale is a legacy Service Sale (with status SERVICE)
 */
export const isLegacyServiceSale = (status?: SaleStatus | string) => {
  return status === SaleStatus.SERVICE
}

/**
 * Return true if any payments have been made against the sale.
 *
 * @return True if the sale has any payments.
 */
export const hasPayments = (
  sale: Pick<ReduxCurrentSale, 'payments'>
): boolean => {
  return Boolean(sale.payments?.length) && sale.payments.length > 0
}

/**
 * Determine if the parked sale is empty (has no customer/line items/note)
 *
 * @return True if current sale is empty parked sale and not a return, false otherwise.
 */
export const isEmptyParkedSale = (
  sale: Pick<
    ReduxCurrentSale,
    'payments' | 'note' | 'customer' | 'lineItems' | 'status' | 'returnFor'
  >
): boolean => {
  return Boolean(
    isEmptySale(sale) &&
      sale.status === SaleStatus.PARKED &&
      !hasPayments(sale) &&
      !isReturn(sale)
  )
}

/**
 * Determine whether this sale is completed or not. A completed sale is one that has either been fully paid
 * off and closed or has been voided.
 *
 * @return {Boolean} True if this sale is in a completed state, false otherwise
 */
export const isCompleted = (status?: SaleStatus | string): boolean => {
  return COMPLETED_STATUSES.includes(status as SaleStatus)
}

/**
 * Determine if sale is in a confirmed state and is ready to be completed
 * @return True is the sale is in a confirmed, ready to complete state, false otherwise
 */
export const isConfirmedReadyToComplete = (
  sale: Pick<
    ReduxCurrentSale,
    'status' | 'readyToComplete' | 'isSubmittedSaleEarly'
  >
): boolean => {
  return (
    isConfirmed(sale) && (sale.readyToComplete || sale.isSubmittedSaleEarly)
  )
}

/**
 * Determine if the local sale is in a confirmed state and but not ready to complete
 * @return True is the sale is in a confirmed, but not ready to complete
 */
export const isConfirmedNotReadyToComplete = (
  sale: Pick<
    ReduxCurrentSale | Sale,
    'status' | 'readyToComplete' | 'isSubmittedSaleEarly'
  >
): boolean => {
  return (
    isConfirmed(sale) && !sale.readyToComplete && !sale.isSubmittedSaleEarly
  )
}

/**
 * Determine whether this sale is "confirmed" or not. A confirmed sale is a Layby or On Account sale that
 * has not been completed (paid off) yet.
 *
 * @return True if this sale is in a confirmed state, false otherwise
 */
export const isConfirmed = (
  sale: Pick<ReduxCurrentSale | Sale, 'status'>
): boolean => {
  return CONFIRMED_STATUSES.map(String).includes(sale.status || '')
}

/**
 * Determine whether this sale is a quoted sale.
 *
 * @return True if this sale is in a quote state, false otherwise
 */
export const isQuote = (status?: string): status is SaleStatus => {
  if (!status) {
    return false
  }

  return status === SaleStatus.QUOTE
}

/**
 * Determine whether this sale is an unfulfilled sale
 *
 * @return True if this sale is in a unfulfilled state, false otherwise
 */
export const isMarkedAsUnfulfilled = (
  status?: SaleStatus | string
): boolean => {
  return UNFULFILLED_STATUSES.includes(status as SaleStatus)
}

/**
 * Calculate the total amount of payments.
 *
 * @return {Number} How much has been paid.
 */
export const getPaymentsTotal = (payments: Payment[]): number => {
  return payments.reduce((total, payment) => {
    const { amount } = payment
    return add(total, amount)
  }, 0)
}

/**
 * Whether this sale has a non-zero discount.
 *
 * @return {Boolean} true if this sale has a discount, false otherwise
 */
export const hasDiscount = (
  discount?: SaleDiscount | null
): discount is SaleDiscount => {
  if (!discount) {
    return false
  }

  return Boolean(discount && Number(discount.price))
}

/**
 * Check if a sale is confirmed
 */
export const isConfirmedSale = (
  sale: Pick<ReduxCurrentSale, 'status'>
): boolean => {
  return sale.status ? CONFIRMED_STATUSES.includes(sale.status) : false
}

/**
 * Check if a sale is active sale
 */
export const isActiveSale = (
  sale: Pick<ReduxCurrentSale, 'id' | 'localId'>
): boolean => {
  return !!(sale.id || sale.localId)
}

/**
 * Determine whether this sale is "Layby" or not. This will included both
 * confirmed layby and layby closed sales
 */
export const isLayby = (saleStatus?: string): saleStatus is SaleStatus => {
  if (!saleStatus) {
    return false
  }

  return [SaleStatus.LAYBY_SALE, SaleStatus.LAYBY_SALE_CLOSED].includes(
    saleStatus as SaleStatus
  )
}

/**
 * Return true if the sale contains a Gift Card line item with the specified number
 */
export const containsGiftCardByNumber = (
  lineItems: Readonly<LineItem[]>,
  number: string
) => {
  return lineItems.some(
    lineItem =>
      isGiftCard(lineItem.product) && lineItem.giftCardNumber === number
  )
}

type GetOutstandingBalanceParams = Pick<
  ReduxCurrentSale,
  'finals' | 'payAccountBalance' | 'payments'
>

/**
 * Calculate the outstanding balance by subtracting every payment from the
 * inclusive total.
 *
 * @return {Number} How much still remains to pay on this sale.
 */
export const getOutstandingBalance = ({
  finals,
  payAccountBalance,
  payments,
}: GetOutstandingBalanceParams) => {
  // Add account balance payment amount to the total balance for this sale,
  // otherwise set totalBalance to be the finals.inclusivePrice of the sale.
  const totalBalance = payAccountBalance?.amount
    ? add(finals.inclusivePrice, payAccountBalance.amount)
    : finals.inclusivePrice
  const totalPayments = getPaymentsTotal(payments)

  return subtract(totalBalance, totalPayments)
}

// Todo: Remove the support for status as string
// This is temporary until we get rid of `Sale`
type IsPartialPaidSaleParams = Pick<
  ReduxCurrentSale,
  'payments' | 'finals' | 'payAccountBalance'
> & { status?: SaleStatus | string }

/**
 * Responsible for determining whether the sale is PUFs (Partially Paid
 * Unfulfilled Sale) or Partially Paid Service Sale
 *
 * @return {Boolean} True if the sale is PUFs or Partially Paid Service Sale,
 * false otherwise.
 */
export const isPartialPaidSale = ({
  status,
  payments,
  finals,
  payAccountBalance,
}: IsPartialPaidSaleParams): boolean => {
  return (
    (isMarkedAsUnfulfilled(status) || isLegacyServiceSale(status)) &&
    getOutstandingBalance({
      finals,
      payments,
      payAccountBalance,
    }) > 0 &&
    ((!isAccountBalancePaymentAdded(payAccountBalance) &&
      getPaymentsTotal(payments) > 0) ||
      (isAccountBalancePaymentPaid(payAccountBalance) &&
        vn(getPaymentsTotal(payments)).gt(payAccountBalance!.amount)))
  )
}

type GetOutstandingBalanceAfterChangeParams = Pick<
  ReduxCurrentSale,
  'finals' | 'payments' | 'payAccountBalance' | 'change'
>

/**
 * Returns the outstanding balance after change has been applied as a payment.
 *
 * @return {Number} Amount remaining to pay on this sale, after change is given.
 */
export const getOutstandingBalanceAfterChange = ({
  finals,
  payments,
  payAccountBalance,
  change,
}: GetOutstandingBalanceAfterChangeParams): number => {
  const outstandingBalance = getOutstandingBalance({
    finals,
    payments,
    payAccountBalance,
  })

  if (change && hasChange(change)) {
    return subtract(outstandingBalance, change.amount)
  } else {
    return outstandingBalance
  }
}

/**
 * Determine if the sale has an outstanding balance
 *
 * @return {Boolean} Returns true if the sale has an outstanding balance, false otherwise
 */
export const hasOutstandingBalance = ({
  finals,
  payments,
  payAccountBalance,
  change,
}: GetOutstandingBalanceAfterChangeParams): boolean => {
  const outstandingBalanceAfterChange = getOutstandingBalanceAfterChange({
    finals,
    payments,
    payAccountBalance,
    change,
  })
  return Boolean(!vn(outstandingBalanceAfterChange).eq(0))
}

/**
 * Determine if the sale has line items.
 *
 * @return True if there are line items, false otherwise.
 */
export const hasLineItems = (
  sale: Pick<ReduxCurrentSale, 'lineItems'>
): boolean => {
  return sale.lineItems.ids.length > 0
}

/**
 * Determine if the sale has any unknown line items.
 *
 * @return True if there are unknown line items, false otherwise.
 */
export const hasUnknownLineItems = (
  sale: Pick<ReduxCurrentSale, 'unknownLineItems'>
): boolean => {
  return Boolean(sale.unknownLineItems.length)
}

type IsOnAccountLimitReachedParams = Pick<
  ReduxCurrentSale,
  'customerBalances' | 'payAccountBalance' | 'payments' | 'finals'
>
/**
 * Check if the On Account (OA) limit has been reached.
 *
 * @return {Boolean} True if OA limit has been reached, false otherwise
 */
export const isOnAccountLimitReached = ({
  customerBalances,
  payAccountBalance,
  payments,
  finals,
}: IsOnAccountLimitReachedParams): boolean => {
  if (typeof customerBalances?.availableOnAccountLimitBalance === 'number') {
    const hasMadeBalancePayment =
      payAccountBalance?.amount && payAccountBalance?.paymentId
    const paidAccountBalance = payAccountBalance?.amount || 0
    const outstandingBalance = getOutstandingBalance({
      finals,
      payAccountBalance,
      payments,
    })
    const currentCustomerBalance = add(
      customerBalances.availableOnAccountLimitBalance,
      hasMadeBalancePayment ? paidAccountBalance : 0
    )

    return outstandingBalance > currentCustomerBalance
  }
  return false
}

/**
 * Get the serviceAgenda for this sale split in {date, scheduledTime: {hours, minutes}}
 * @return {Object} The UI ready serviceAgenda for this sale
 */
export const getServiceAgenda = (
  sale: Pick<ReduxCurrentSale, 'status' | 'serviceFields' | 'customFields'>
) => {
  const { status, serviceFields, customFields } = sale

  if (!serviceFields) {
    return
  }

  if (!hasServiceOrderWithAgenda({ status, serviceFields, customFields })) {
    return
  }

  const scheduledTime = toHoursAndMinutes(
    serviceFields.serviceAgenda!.minutesScheduled
  )

  const serviceAgenda = {
    date: formatDateTime(serviceFields.serviceAgenda!.date, DATE_MEDIUM_FULL),
    scheduledTime,
  }

  return serviceAgenda
}

export type GetServiceAgendaBannerTextParams = Pick<
  ReduxCurrentSale,
  'status' | 'serviceFields' | 'customFields' | 'isSavedService'
>

/**
 * Get the service agenda banner text
 */
export const getServiceAgendaBannerText = (
  sale: GetServiceAgendaBannerTextParams
) => {
  const agenda = getServiceAgenda({
    status: sale.status,
    serviceFields: sale.serviceFields,
    customFields: sale.customFields,
  })
  const isSavedService = sale.isSavedService
  const dotDisplay = isSavedService ? '' : '.'
  const { itemName } = sale.serviceFields?.serviceItem?.itemDetails ?? {
    item: undefined,
  }

  // Get the text for a completed service
  const getCompletedServiceText = () =>
    itemName
      ? `This service for "${itemName}" is completed`
      : `This service is completed`

  // Get the text for an unscheduled service
  const getUnscheduledServiceText = () =>
    itemName
      ? `This service is for "${itemName}"${dotDisplay}`
      : `This sale contains an unscheduled service${dotDisplay}`

  // Get the text for a scheduled service
  const getScheduledServiceText = (
    hoursDisplay: string,
    minutesDisplay: string,
    verbDisplay: string
  ) => {
    return itemName
      ? `This ${hoursDisplay}${minutesDisplay}service ${verbDisplay} scheduled on ${date} for "${itemName}"${dotDisplay}`
      : `This ${hoursDisplay}${minutesDisplay}service ${verbDisplay} scheduled on ${date}${dotDisplay}`
  }

  if (isServiceStatusFinal(sale.serviceFields)) {
    return getCompletedServiceText()
  }

  if (!agenda || Object.keys(agenda).length === 0) {
    return getUnscheduledServiceText()
  }

  const {
    date,
    scheduledTime: { hours, minutes },
  } = agenda

  const hoursDisplay = hours ? hours + ' h ' : ''
  const minutesDisplay = minutes ? minutes + ' min ' : ''
  const verbDisplay = isSavedService ? 'is' : 'will be'

  return getScheduledServiceText(hoursDisplay, minutesDisplay, verbDisplay)
}

/**
 * Determines what to display for the surcharge rule
 * @returns either the surcharge rule or the number of surcharge rules
 */
export const displaySurchargeRule = (sale: Pick<Sale, 'finals'>): string => {
  if (!sale.finals.surchargeDisplayProperties?.surchargeRules?.length) {
    return ''
  }

  const { surchargeRules } = sale.finals.surchargeDisplayProperties

  // removing duplicate rules
  const uniqueRules = [...new Set(surchargeRules)]

  // For capped surcharge, we don't want to display the rule
  if (uniqueRules.includes('')) {
    return ''
  }

  return uniqueRules.length <= 1
    ? uniqueRules[0]
    : `${uniqueRules.length} rates`
}

/**
 * Gets the label for surcharge
 * FIXME: remove this function once the hook useTranslatedSurchargePaymentLabel is being used in replacement of this
 */
export const getSurchargeLabel = (sale: Pick<Sale, 'finals'>): string => {
  const showTax = vn(sale.finals.tax).eq(0) ? '' : ' (tax incl.)'
  return `Payment surcharge ${displaySurchargeRule(sale)}${showTax}`
}

/**
 * Calculates the sale total when cash discounting is enabled; subtracting the calculated cash discount from the
 * inclusive price.
 *
 * @return {Number} The sale inclusive price with the total calculated cash discount subtracted
 */
export const getCashDiscountedSaleTotal = (
  sale: Pick<Sale, 'finals' | 'totals'>
): number => {
  return subtract(sale.finals.inclusivePrice, sale.totals.cashDiscount)
}

/**
 * Calculate how much change needs to be returned, which happens when payments exceed inclusive total. This is a
 * display value so it is absolute.
 *
 * @returns {Number} The absolute change to return when completing this sale
 */
export const getChangeAmountForDisplay = (
  sale: Pick<ReduxCurrentSale, 'change'>
): number => {
  return sale.change ? vn(sale.change.amount).abs().toNumber() : 0
}

/**
 * Determine whether this sale is closed or not. A closed sale is a normal, layby or on account sale that
 * has been fully paid off and completed.
 *
 * @return True if this sale is in a closed state, false otherwise
 */
export const isClosed = (status?: string): status is SaleStatus => {
  if (!status) {
    return false
  }

  return Object.values(CLOSED_STATUSES).includes(status as SaleStatus)
}

/**
 * Determine if the sale is open. An open sale is a sale with a status of either layby, onaccount, parked,
 * exchange (parked) or a return (parked).
 *
 * @return {Boolean} True if this sale is in an open state, false otherwise
 */
export const isOpen = (status?: string): status is SaleStatus => {
  if (!status) {
    return false
  }

  return Object.values(OPEN_STATUSES).includes(status as SaleStatus)
}

/**
 * Determine whether this sale has been voided or not.
 *
 * @return {Boolean} True if this sale is in a voided state, false otherwise
 */
export const isVoided = (status?: string): status is SaleStatus => {
  if (!status) {
    return false
  }

  return status === SaleStatus.VOIDED
}

/**
 * Determine whether this sale is a parked / saved sale.
 *
 * @return True if this sale is in a saved state, false otherwise
 */
export const isParked = (status?: string): status is SaleStatus => {
  if (!status) {
    return false
  }

  return status === SaleStatus.PARKED
}

/**
 * Determine whether this sale is "on account" or not. This will included both
 * confirmed on account and on account closed sales
 *
 * @return True if this sale is in a confirmed / closed on account state, false
 * otherwise
 */
export const isOnAccount = (status?: string): status is SaleStatus => {
  if (!status) {
    return false
  }

  return [SaleStatus.ACCOUNT_SALE, SaleStatus.ACCOUNT_SALE_CLOSED].includes(
    status as SaleStatus
  )
}

/**
 * Determine if this is sale is "on account" and has no fulfillment type defined.
 *
 * @return True if this is an on account sale with no fulfillment type defined, false otherwise
 */
export const isNonFulfillmentOnAccountSale = (
  sale: Pick<ReduxCurrentSale, 'fulfilmentDetails' | 'status'>
): boolean => {
  const isFulfillmentSale = isDefinedFulfilmentType(
    sale.fulfilmentDetails[0]?.type
  )

  return isOnAccount(sale.status) && !isFulfillmentSale
}

type IsUnpaidSaleParams = Pick<
  ReduxCurrentSale,
  'payments' | 'payAccountBalance'
> & { status?: SaleStatus | string }

/**
 * Responsible for determining whether the sale is an unpaid unfulfilled  or
 * service sale (no payment at all).
 *
 * @returns {Boolean} True if the sale is an unpaid unfulfilled or service
 * sale, false otherwise.
 */
export const isUnpaidSale = (sale: IsUnpaidSaleParams): boolean => {
  const paymentsTotal = getPaymentsTotal(sale.payments)

  return (
    (isMarkedAsUnfulfilled(sale.status) || isLegacyServiceSale(sale.status)) &&
    ((!isAccountBalancePaymentAdded(sale.payAccountBalance) &&
      paymentsTotal === 0) ||
      (isAccountBalancePaymentPaid(sale.payAccountBalance) &&
        vn(paymentsTotal).eq(sale.payAccountBalance?.amount!)))
  )
}

/**
 * Find line item in the sale by localId
 */
export const findLineItemByLocalId = (sale: Sale, localId: string) =>
  sale.lineItems.find(li => li.localId === localId)
/**
 * Determine whether the sale is up to date with the server (either has never been modified on this client
 * or those modifications have been successfully synced).
 */
export const isSynced = (sale: Pick<ReduxCurrentSale, 'syncStatus'>): boolean =>
  SYNCED_SYNC_STATUSES.includes(sale.syncStatus)

/**
 * Check if current sale is uploaded but not completed
 */
export const isUploadedToBeCompleted = (
  sale: Pick<ReduxCurrentSale, 'syncStatus'>
): boolean => sale.syncStatus === SaleSyncStatus.UPLOADED_TO_COMPLETE

/**
 * Determine if the sale total is 0
 */
export const isSaleTotalZero = (sale: Pick<ReduxCurrentSale, 'finals'>) =>
  Boolean(vn(sale.finals.inclusivePrice).eq(0))

/**
 * Determine if the sale total is > 0
 */
export const isSaleTotalGreaterThanZero = (
  sale: Pick<ReduxCurrentSale, 'finals'>
) => Boolean(vn(sale.finals.inclusivePrice).gt(0))

/**
 * Determine if the sale total is less than 0
 */
export const isSaleTotalLessThanZero = (
  sale: Pick<ReduxCurrentSale, 'finals'>
) => Boolean(vn(sale.finals.inclusivePrice).lt(0))

/**
 * Generate a random short code for the sale. Used for loyalty claim urls.
 *
 * @return {String} The six digit loyalty code
 */
export const generateSaleLoyaltyClaimCode = (): string => {
  let shortCode = ''

  for (let i = 0; i < SHORT_CODE_LENGTH; i++) {
    const charIndex = Math.floor(Math.random() * SHORT_CODE_CHARS.length)
    shortCode += SHORT_CODE_CHARS.charAt(charIndex)
  }

  return shortCode
}
/**
 * Generate the formatted receipt number details from its parts.
 */
export const getFormattedReceiptNumberDetails = ({
  receiptNumberDetails,
}: Pick<ReduxCurrentSale, 'receiptNumberDetails'>) => {
  if (typeof receiptNumberDetails === 'string') {
    return receiptNumberDetails
  } else if (receiptNumberDetails) {
    return (
      receiptNumberDetails.prefix +
      receiptNumberDetails.number +
      receiptNumberDetails.suffix
    )
  } else {
    return ''
  }
}

/**
 * Check if current sale is submit early and it's the first submission
 */
export const isFirstTimeSubmitEarly = (
  sale: Pick<ReduxCurrentSale, 'syncStatus'>
) => sale.syncStatus === SaleSyncStatus.UPLOAD_FIRST_SUBMIT

/**
 * Generate the formatted cash discounting adjustments for the sale.
 *
 * @return {Array} adjustments array
 */
export const getCashDiscountingAdjustments = (
  sale: Pick<Sale, 'payments' | 'change'>,
  lineItems: LineItem[],
  cashDiscountingRate: string
) => {
  const salePayments = sale.payments.map((payment, index) => {
    let amount = payment.amount

    // We have to adjust the last payment amount when the sale has change. Without this, the calculated adjustment
    // would be greater than the actual cash discount applied (since the customer has overpaid). Since overpaying
    // finalizes the sale, assume the last payment is the one that should be adjusted.
    if (hasChange(sale.change) && index === sale.payments.length - 1) {
      // Change is a negative amount
      amount = VendNumber.add(amount, sale.change?.amount || 0)
    }

    // We have to adjust the payment amount when the payment has tip and service fee. Without this, the calculated
    // adjustment would be greater than the actual non_cash_fee applied (plus the tip amount).
    if (paymentHasTip(payment)) {
      amount = VendNumber.subtract(amount, payment.tipAmount!)
    }

    return {
      id: payment.id || payment.localId,
      paymentTypeId: payment.paymentType.type_id,
      amount,
    }
  })

  const cashDiscountingPayments = salePayments.filter(payment =>
    isEligibleForCashDiscount(payment.paymentTypeId)
  )
  const nonCashDiscountPayments = salePayments.filter(
    payment => !isEligibleForCashDiscount(payment.paymentTypeId)
  )
  const cardProcessingRate = VendNumber.divide(
    cashDiscountingRate,
    VendNumber.add(1, cashDiscountingRate)
  )
  const lineItemIds = lineItems.map(lineItem => lineItem.id || lineItem.localId)

  const nonCashPaymentTypeAdjustments = nonCashDiscountPayments.map(
    payment => ({
      ...NON_CASH_FEE,
      source_id: payment.id,
      targets: lineItemIds,
      value: VendNumber.round(
        VendNumber.multiply(payment.amount, cardProcessingRate).toString()
      ),
    })
  )

  const positiveAdjustments = cashDiscountingPayments.map(payment => ({
    ...NON_CASH_FEE,
    source_id: payment.id,
    targets: lineItemIds,
    value: VendNumber.round(
      VendNumber.multiply(payment.amount, cashDiscountingRate).toString()
    ),
  }))

  const negativeAdjustments = cashDiscountingPayments.map(payment => ({
    ...NON_CASH_FEE,
    source_id: payment.id,
    targets: lineItemIds,
    value: `-${VendNumber.round(
      VendNumber.multiply(payment.amount, cashDiscountingRate)
    )}`,
  }))

  return [
    ...nonCashPaymentTypeAdjustments,
    ...positiveAdjustments,
    ...negativeAdjustments,
  ]
}

/**
 * Determines if the sale has any tips associated with currently made payments (current payments made with tip on the sell screen)
 */
export const paymentsHasTip = (sale: Pick<Sale, 'payments'>) => {
  return sale.payments.some(paymentHasTip)
}

/**
 * Generate the formatted tip adjustments for the sale.
 */
export const getTipAdjustments = (sale: Pick<Sale, 'payments'>) => {
  const paymentsWithTips = sale.payments.filter(payment =>
    vn(payment.tipAmount).gt(0)
  )

  return paymentsWithTips.map(payment => ({
    ...TIP_ADJUSTMENT,
    source_id: payment.localId,
    value: payment.tipAmount!.toString(),
  }))
}

/**
 * Generate the formatted cash discounting adjustments for createSaleMutation input.
 */
export const getCashDiscountingAdjustmentsForMutation = (
  sale: Pick<Sale | ReduxCurrentSale, 'payments' | 'change'>,
  lineItems: LineItem[],
  cashDiscountingRate: string
): GqlAdjustment[] => {
  const salePayments = sale.payments.map((payment, index) => {
    let amount = payment.amount

    // We have to adjust the last payment amount when the sale has change. Without this, the calculated adjustment
    // would be greater than the actual cash discount applied (since the customer has overpaid). Since overpaying
    // finalizes the sale, assume the last payment is the one that should be adjusted.
    if (
      hasChange(sale.change) &&
      sale.change &&
      index === sale.payments.length - 1
    ) {
      // Change is a negative amount
      amount = VendNumber.add(amount, sale.change.amount)
    }

    // We have to adjust the payment amount when the payment has tip and service fee. Without this, the calculated
    // adjustment would be greater than the actual non_cash_fee applied (plus the tip amount).
    if (paymentHasTip(payment)) {
      amount = VendNumber.subtract(amount, payment.tipAmount!)
    }

    return {
      id: payment.id || payment.localId,
      paymentTypeId: payment.paymentType.type_id,
      amount,
    }
  })

  const cashDiscountingPayments = salePayments.filter(payment =>
    isEligibleForCashDiscount(payment.paymentTypeId)
  )
  const nonCashDiscountPayments = salePayments.filter(
    payment => !isEligibleForCashDiscount(payment.paymentTypeId)
  )
  const cardProcessingRate = VendNumber.divide(
    cashDiscountingRate,
    VendNumber.add(1, cashDiscountingRate)
  )
  const lineItemIds = lineItems.map(lineItem => lineItem.id || lineItem.localId)

  const nonCashPaymentTypeAdjustments = nonCashDiscountPayments.map(
    payment => ({
      ...NON_CASH_FEE_MUTATION,
      sourceID: payment.id,
      targets: lineItemIds,
      value: VendNumber.round(
        VendNumber.multiply(payment.amount, cardProcessingRate)
      ),
    })
  )

  const positiveAdjustments = cashDiscountingPayments.map(payment => ({
    ...NON_CASH_FEE_MUTATION,
    sourceID: payment.id,
    targets: lineItemIds,
    value: VendNumber.round(
      VendNumber.multiply(payment.amount, cashDiscountingRate)
    ),
  }))

  const negativeAdjustments = cashDiscountingPayments.map(payment => ({
    ...NON_CASH_FEE_MUTATION,
    sourceID: payment.id,
    targets: lineItemIds,
    value: `-${VendNumber.round(
      VendNumber.multiply(payment.amount, cashDiscountingRate)
    )}`,
  }))

  return [
    ...nonCashPaymentTypeAdjustments,
    ...positiveAdjustments,
    ...negativeAdjustments,
  ]
}

/**
 * Generate the formatted tip adjustments for the createSaleMutaion input.
 */
export const getTipAdjustmentsForMutation = (
  sale: Pick<ReduxCurrentSale | Sale, 'payments'>
): GqlAdjustment[] => {
  const paymentsWithTips = sale.payments.filter(payment =>
    vn(payment.tipAmount).gt(0)
  )

  return paymentsWithTips.map(payment => ({
    ...TIP_ADJUSTMENT_MUTATION,
    sourceID: payment.localId,
    value: payment.tipAmount!.toString(),
  }))
}

/**
 * If there is any surcharge needs to be refunded
 */
export const hasSurchargeRefundable = (
  sale: Pick<ReduxCurrentSale, 'finals'>
) => {
  return vn(sale.finals.surchargeDisplayProperties.surcharge).lt(0)
}

/**
 * Returns a JSON representation of this sale suitable for storing in IndexedDB.
 */
export const toJSON = (sale: ReduxCurrentSale): SaleJson => {
  const lineItems = lineItemsAdapter.getSelectors().selectAll(sale.lineItems)
  const reduxSaleStorageAdapter = new ReduxSaleStorageAdapter(sale, lineItems)

  return reduxSaleStorageAdapter.toJSON()
}

/**
 * Determine whether this sale is a new created unfulfilled sale
 */
export const isNotSavedUnfulfilled = (
  sale: Pick<ReduxCurrentSale, 'status' | 'isSavedUnfulfilled'>
) => {
  return isMarkedAsUnfulfilled(sale.status) && !sale.isSavedUnfulfilled
}

/**
 * Determine whether this sale is an unfulfilled pickup sale
 */
export const isUnfulfilledPickUp = (status?: string): status is SaleStatus => {
  return status === SaleStatus.AWAITING_PICKUP
}

export const isDismissable = (
  sale: Pick<
    ReduxCurrentSale | Sale,
    | 'status'
    | 'readyToComplete'
    | 'isSubmittedSaleEarly'
    | 'isSavedUnfulfilled'
    | 'isSavedService'
  >
) => {
  const dismissableSaleStatuses = [
    SaleStatus.LAYBY_SALE,
    SaleStatus.ACCOUNT_SALE,
    SaleStatus.AWAITING_DISPATCH,
    SaleStatus.AWAITING_PICKUP,
    SaleStatus.SERVICE,
  ]

  if (
    isConfirmedNotReadyToComplete(sale) ||
    sale.isSavedUnfulfilled ||
    sale.isSavedService
  ) {
    if (
      sale.status &&
      dismissableSaleStatuses.includes(sale.status as SaleStatus)
    ) {
      return true
    }
  }

  return false
}

export const isUnfulfilledLineItem = (
  fufillmentLifEnabled: boolean,
  lineItem: Pick<LineItem, 'fulfilmentType'>,
  saleStatus?: SaleStatus | string
) => {
  if (fufillmentLifEnabled) {
    return isDefinedFulfilmentType(lineItem.fulfilmentType)
  }
  return isMarkedAsUnfulfilled(saleStatus)
}

export const isNotHiddenPayment = (payment: Payment) => {
  return HIDDEN_PAYMENT_TYPES.indexOf(payment.paymentType.type_id) === -1
}

export const hasEcomCustomCharges = (sale: Sale) =>
  sale.ecomCustomCharges &&
  (!vn(sale.ecomCustomCharges.totalIncl).eq(0) || sale.hasEcomCustomCharges)
